import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDT, daysLeft } from "@/lib/api";
import { PageHeader, ProgressBar, StatusBadge, Loading, EmptyState } from "@/components/Shared";
import { Search, Clock, Users, ShoppingCart, Filter } from "lucide-react";

export default function Catalogue() {
  const [ops, setOps] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [sup, setSup] = useState("");
  const [status, setStatus] = useState("open");

  const load = () => {
    const params = {};
    if (cat) params.category = cat;
    if (sup) params.supplier_id = sup;
    if (status) params.status = status;
    if (q) params.q = q;
    api.get("/operations", { params }).then((r) => setOps(r.data)).catch(() => setOps([]));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat, sup, status]);
  useEffect(() => { api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

  const categories = useMemo(() => [...new Set((ops || []).map((o) => o.category).filter(Boolean))], [ops]);

  return (
    <div>
      <PageHeader title="Achats groupés" subtitle="Participez aux opérations négociées par la plateforme." />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="catalogue-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Rechercher une opération..." className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow text-sm" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="filter-category" className="h-10 px-3 rounded-lg border border-slate-300 text-sm font-medium bg-white">
          <option value="">Toutes catégories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={sup} onChange={(e) => setSup(e.target.value)} data-testid="filter-supplier" className="h-10 px-3 rounded-lg border border-slate-300 text-sm font-medium bg-white">
          <option value="">Tous fournisseurs</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="filter-status" className="h-10 px-3 rounded-lg border border-slate-300 text-sm font-medium bg-white">
          <option value="open">Ouvertes</option>
          <option value="closed">Clôturées</option>
          <option value="">Toutes</option>
        </select>
      </div>

      {ops === null ? <Loading /> : ops.length === 0 ? (
        <EmptyState title="Aucune opération" subtitle="Aucune opération ne correspond à vos filtres." icon={Filter} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="catalogue-grid">
          {ops.map((op) => {
            const dl = daysLeft(op.close_date);
            const isOpen = op.status === "open";
            return (
              <div key={op.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col" data-testid={`op-card-${op.id}`}>
                <div className="p-5 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{op.category || "Général"}</span>
                    <StatusBadge status={op.status} />
                  </div>
                  <h3 className="mt-3 font-bold text-slate-900 leading-snug line-clamp-2">{op.name}</h3>
                  <div className="text-sm text-slate-400 mt-0.5">{op.supplier_name}</div>
                  <div className="mt-4"><ProgressBar current={op.committed_amount} target={op.objective_amount} /></div>
                  <div className="mt-4 flex items-center justify-between text-xs font-semibold">
                    <span className="flex items-center gap-1 text-slate-500"><Users className="h-3.5 w-3.5" />{op.participants_count} participants</span>
                    <span className={`flex items-center gap-1 ${dl > 3 ? "text-slate-500" : "text-amber-600"}`}><Clock className="h-3.5 w-3.5" />{dl > 0 ? `${dl} j restants` : "Clôturée"}</span>
                  </div>
                </div>
                <Link to={`/operations/${op.id}`} data-testid={`participate-${op.id}`}
                  className={`m-4 mt-0 h-11 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors duration-200 ${isOpen ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  <ShoppingCart className="h-4 w-4" /> {isOpen ? "Participer" : "Consulter"}
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
